import MainSectionSection from "@/components/MainSectionSection";
import AddUserModal from "@/components/AddUserModal";
import PipelinePermissionsModal from "@/components/PipelinePermissionsModal";
import EmployeeLinksModal from "@/components/EmployeeLinksModal";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { User, Plus, Trash2, Edit2, Link2 } from "lucide-react";
import { trpc, RouterOutputs } from "@/trpc";
import { useState, useContext, useMemo } from "react";
import UserContext from "@/contexts/UserContext";
import MerchantContext from "@/contexts/MerchantContext";
import { mapPipelineName } from "@/utils/valueMap";
import { ADMIN_EMPLOYEE_ROLE, isDataScopedEmployee, type EmployeeRole } from "@/utils/permissions";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

const getStatusBadgeClasses = (status: string) => {
  return status === "active"
    ? "rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800"
    : "rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-800";
};

const pipelineBadgeColors = [
  "bg-blue-100 text-blue-800",
  "bg-teal-100 text-teal-800",
  "bg-cyan-100 text-cyan-800",
  "bg-purple-100 text-purple-800",
  "bg-pink-100 text-pink-800",
  "bg-orange-100 text-orange-800",
];

const getPipelineBadgeClasses = (index: number) => {
  return cn(
    "rounded px-2 py-0.5 text-xs font-medium",
    pipelineBadgeColors[index % pipelineBadgeColors.length]
  );
};

type UserRow = RouterOutputs["users"]["list"][number];

const NOT_SIGNED_IN_LABEL = "Aún no ha iniciado sesión";

const employeeLabel = (
  employee: UserRow["employees"][number],
  ownEmployeeId: string | undefined
): string => {
  if (!employee.active) return `${employee.name} (inactivo)`;
  return employee.id === ownEmployeeId ? `${employee.name} (propio)` : employee.name;
};

const LinkedEmployeesBadges = ({
  userRow,
  isLoaded,
}: {
  userRow?: UserRow;
  isLoaded: boolean;
}) => {
  if (!isLoaded) return null;
  if (!userRow) return <p className="text-xs text-gray-400">{NOT_SIGNED_IN_LABEL}</p>;
  if (userRow.employees.length === 0) {
    return <p className="text-xs text-gray-400">Sin asesores</p>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {userRow.employees.map((employee) => (
        <span
          key={employee.id}
          className={cn(
            "rounded px-2 py-0.5 text-xs font-medium",
            employee.active ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-500"
          )}
        >
          {employeeLabel(employee, userRow.employeeId)}
        </span>
      ))}
    </div>
  );
};

const LinkEmployeesButton = ({
  hasUserRow,
  isLoaded,
  onClick,
  className,
}: {
  hasUserRow: boolean;
  isLoaded: boolean;
  onClick: () => void;
  className?: string;
}) => {
  const title = !isLoaded || hasUserRow ? undefined : NOT_SIGNED_IN_LABEL;
  return (
    <span title={title} className="inline-flex">
      <Button
        variant="ghost"
        size="icon"
        aria-label="Vincular asesores"
        onClick={onClick}
        disabled={!hasUserRow}
        title={title}
        className={className}
      >
        <Link2 className="size-4" />
      </Button>
    </span>
  );
};

const linkedEmployeesSummary = (userRow?: UserRow): string => {
  if (!userRow) return NOT_SIGNED_IN_LABEL;
  if (userRow.employees.length === 0) return "Sin asesores";
  return userRow.employees
    .map((employee) => employeeLabel(employee, userRow.employeeId))
    .join(", ");
};

const UsersContainer = () => {
  const { roles, hasPalommaOnlyAccess } = useContext(UserContext);
  const { merchantId } = useContext(MerchantContext);
  const isMobile = useIsMobile();
  const canManageUsers = roles.includes(ADMIN_EMPLOYEE_ROLE) || hasPalommaOnlyAccess;

  const [usersData, setUsersData] = useState<RouterOutputs["auth"]["getOrganizationUsers"]>([]);
  const [fetching, setFetching] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<{ id: string; name: string } | null>(null);
  const [userForPipelinePermissions, setUserForPipelinePermissions] = useState<
    RouterOutputs["auth"]["getOrganizationUsers"][number] | null
  >(null);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isPipelinePermissionsOpen, setIsPipelinePermissionsOpen] = useState(false);
  const [userForEmployeeLinks, setUserForEmployeeLinks] = useState<{
    user: RouterOutputs["auth"]["getOrganizationUsers"][number];
    userRow: UserRow;
  } | null>(null);
  const [isEmployeeLinksOpen, setIsEmployeeLinksOpen] = useState(false);

  const { data: userRows } = trpc.users.list.useQuery(
    { merchantId },
    { enabled: !!merchantId }
  );
  const userRowsLoaded = !!userRows;
  const userRowByMemberId = useMemo(
    () => new Map((userRows ?? []).map((userRow) => [userRow.stytchMemberId, userRow])),
    [userRows]
  );

  const users = trpc.auth.getOrganizationUsers.useQuery(undefined, {
    onSuccess: (data) => {
      setUsersData(data);
      setFetching(false);
    },
    onError: (error) => {
      console.error("Error fetching users:", error);
      setFetching(false);
    },
  });

  const createMember = trpc.auth.createOrganizationMember.useMutation({
    onSuccess: () => {
      users.refetch();
    },
  });

  const deleteMember = trpc.auth.deleteOrganizationMember.useMutation({
    onSuccess: () => {
      toast.success("Usuario eliminado", {
        description: "El usuario se elimino correctamente de la organizacion",
      });
      users.refetch();
    },
    onError: (error) => {
      toast.error("Error al eliminar usuario", {
        description: error.message || "No se pudo eliminar el usuario",
      });
    },
  });

  const handleAddUser = async (
    name: string,
    email: string,
    role: EmployeeRole
  ) => {
    await createMember.mutateAsync({ name, email, role });
  };

  const handleDeleteClick = (userId: string, userName: string) => {
    setUserToDelete({ id: userId, name: userName });
    setIsDeleteOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (userToDelete) {
      await deleteMember.mutateAsync({ memberId: userToDelete.id });
      setUserToDelete(null);
      setIsDeleteOpen(false);
    }
  };

  const handleEditUser = (user: RouterOutputs["auth"]["getOrganizationUsers"][number]) => {
    setUserForPipelinePermissions(user);
    setIsPipelinePermissionsOpen(true);
  };

  const handleLinkEmployees = (user: RouterOutputs["auth"]["getOrganizationUsers"][number]) => {
    const userRow = userRowByMemberId.get(user.id);
    if (!userRow) return;
    setUserForEmployeeLinks({ user, userRow });
    setIsEmployeeLinksOpen(true);
  };

  return (
    <MainSectionSection gap="24px">
      <div className="rounded-lg border p-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-2">
            <h3 className="text-lg font-semibold text-gray-900">
              Usuarios de la Organizacion ({usersData.length})
            </h3>
            {canManageUsers && (
              <Button
                onClick={() => setIsModalOpen(true)}
                className="w-full min-h-11 md:w-auto"
              >
                <Plus className="size-4" />
                Agregar usuario
              </Button>
            )}
          </div>

          {fetching ? (
            <div className="flex items-center justify-center py-12">
              <div className="flex flex-col items-center gap-4">
                <Spinner size="xl" />
                <p className="text-gray-600">Cargando usuarios...</p>
              </div>
            </div>
          ) : (
            <div className="rounded-lg border">
              <div className="flex flex-col">
                {/* Header de la tabla -- hidden on mobile */}
                {!isMobile && (
                  <div className="flex border-b border-gray-200 bg-gray-50 p-4 text-sm font-semibold text-gray-700">
                    <div style={{ flex: "0 0 30%" }} className="flex items-center">
                      Usuario
                    </div>
                    <div style={{ flex: "0 0 12%" }} className="flex items-center">
                      Estado
                    </div>
                    <div style={{ flex: "0 0 16%" }} className="flex items-center">
                      Rol
                    </div>
                    <div style={{ flex: "0 0 16%" }} className="flex items-center">
                      Pipelines
                    </div>
                    <div style={{ flex: "0 0 16%" }} className="flex items-center">
                      Asesores
                    </div>
                    {canManageUsers && (
                      <div style={{ flex: "0 0 10%" }} className="flex items-center justify-center">
                        Acciones
                      </div>
                    )}
                  </div>
                )}

                {/* Filas de usuarios */}
                {usersData.length === 0 ? (
                  <div className="flex min-h-[200px] items-center justify-center p-8">
                    <div className="flex flex-col items-center gap-2">
                      <p className="text-lg text-gray-500">
                        No se encontraron usuarios
                      </p>
                      <p className="text-sm text-gray-400">
                        No hay usuarios registrados en la organizacion
                      </p>
                    </div>
                  </div>
                ) : (
                  usersData.map(
                    (
                      user: RouterOutputs["auth"]["getOrganizationUsers"][number],
                      index: number
                    ) =>
                      isMobile ? (
                        <div
                          key={user.id}
                          className={cn(
                            "p-3",
                            index < usersData.length - 1 && "border-b border-gray-200"
                          )}
                        >
                          <div className="mb-1 flex items-center justify-between">
                            <div className="flex min-w-0 flex-1 items-center gap-2">
                              <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-purple-100">
                                <User className="size-4 text-purple-600" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="truncate text-sm font-medium">
                                  {user.name || "Sin nombre"}
                                </p>
                                <p className="truncate text-xs text-gray-600">
                                  {user.email}
                                </p>
                              </div>
                            </div>
                            <span className={cn(getStatusBadgeClasses(user.status), "ml-2")}>
                              {user.status === "active" ? "Activo" : "Inactivo"}
                            </span>
                          </div>
                          {userRowsLoaded && (
                            <p className="mt-1 truncate text-xs text-gray-600">
                              Asesores: {linkedEmployeesSummary(userRowByMemberId.get(user.id))}
                            </p>
                          )}
                          <div className="mt-2 flex items-center justify-between">
                            <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                              {user.roles.includes("admin_employee")
                                ? "Administrador"
                                : user.roles.includes("basic_employee")
                                ? "Empleado"
                                : user.roles.includes("restricted_employee")
                                ? "Restringido"
                                : "Sin rol"}
                            </span>
                            {canManageUsers && (
                              <div className="flex gap-1">
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Editar usuario"
                                  onClick={() => handleEditUser(user)}
                                  className="min-h-11 min-w-11"
                                >
                                  <Edit2 className="size-4" />
                                </Button>
                                <LinkEmployeesButton
                                  hasUserRow={userRowByMemberId.has(user.id)}
                                  isLoaded={userRowsLoaded}
                                  onClick={() => handleLinkEmployees(user)}
                                  className="min-h-11 min-w-11"
                                />
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  aria-label="Eliminar usuario"
                                  onClick={() =>
                                    handleDeleteClick(user.id, user.name || user.email)
                                  }
                                  disabled={deleteMember.isLoading}
                                  className="min-h-11 min-w-11 text-red-600 hover:text-red-700"
                                >
                                  <Trash2 className="size-4" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div
                          key={user.id}
                          className={cn(
                            "flex items-center p-4 transition-all hover:bg-gray-50",
                            index < usersData.length - 1 && "border-b border-gray-200"
                          )}
                        >
                          {/* Informacion del usuario */}
                          <div style={{ flex: "0 0 30%" }} className="flex min-w-0 items-center gap-3">
                            <div className="flex size-10 items-center justify-center rounded-full bg-purple-100">
                              <User className="size-5 text-purple-600" />
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-medium text-gray-900">
                                {user.name || "Sin nombre"}
                              </p>
                              <p className="truncate text-xs text-gray-600">
                                {user.email}
                              </p>
                            </div>
                          </div>

                          {/* Estado */}
                          <div style={{ flex: "0 0 12%" }} className="flex items-center">
                            <span className={getStatusBadgeClasses(user.status)}>
                              {user.status === "active" ? "Activo" : "Inactivo"}
                            </span>
                          </div>

                          {/* Rol */}
                          <div style={{ flex: "0 0 16%" }} className="flex items-center">
                            <span className="rounded bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800">
                              {user.roles.includes("admin_employee")
                                ? "Administrador"
                                : user.roles.includes("basic_employee")
                                ? "Empleado"
                                : user.roles.includes("restricted_employee")
                                ? "Restringido"
                                : "Sin rol"}
                            </span>
                          </div>

                          {/* Pipelines */}
                          <div style={{ flex: "0 0 16%" }} className="flex items-center">
                            {isDataScopedEmployee(user.roles) ? (
                              <div className="flex flex-wrap gap-1">
                                {user.pipelinePermissions &&
                                user.pipelinePermissions.length > 0 ? (
                                  user.pipelinePermissions.map((pipeline, pipelineIndex) => (
                                    <span
                                      key={pipeline.id}
                                      className={getPipelineBadgeClasses(pipelineIndex)}
                                    >
                                      {mapPipelineName[
                                        pipeline.id as keyof typeof mapPipelineName
                                      ] || pipeline.id}
                                    </span>
                                  ))
                                ) : (
                                  <p className="text-xs text-gray-400">
                                    Sin pipelines
                                  </p>
                                )}
                              </div>
                            ) : (
                              <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                                Todos
                              </span>
                            )}
                          </div>

                          <div style={{ flex: "0 0 16%" }} className="flex items-center pr-2">
                            <LinkedEmployeesBadges
                              userRow={userRowByMemberId.get(user.id)}
                              isLoaded={userRowsLoaded}
                            />
                          </div>

                          {/* Actions - Only visible for admins and Palomma staff */}
                          {canManageUsers && (
                            <div style={{ flex: "0 0 10%" }} className="flex flex-wrap items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Editar usuario"
                                onClick={() => handleEditUser(user)}
                              >
                                <Edit2 className="size-4" />
                              </Button>
                              <LinkEmployeesButton
                                hasUserRow={userRowByMemberId.has(user.id)}
                                isLoaded={userRowsLoaded}
                                onClick={() => handleLinkEmployees(user)}
                              />
                              <Button
                                variant="ghost"
                                size="icon"
                                aria-label="Eliminar usuario"
                                onClick={() =>
                                  handleDeleteClick(user.id, user.name || user.email)
                                }
                                disabled={deleteMember.isLoading}
                                className="text-red-600 hover:text-red-700"
                              >
                                <Trash2 className="size-4" />
                              </Button>
                            </div>
                          )}
                        </div>
                      )
                  )
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      <AddUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAddUser={handleAddUser}
        existingEmails={usersData.map((user) => user.email)}
      />

      {/* Pipeline Permissions Modal */}
      {userForPipelinePermissions && (
        <PipelinePermissionsModal
          isOpen={isPipelinePermissionsOpen}
          onClose={() => setIsPipelinePermissionsOpen(false)}
          user={userForPipelinePermissions}
          onUpdate={() => users.refetch()}
        />
      )}

      {userForEmployeeLinks && (
        <EmployeeLinksModal
          isOpen={isEmployeeLinksOpen}
          onClose={() => setIsEmployeeLinksOpen(false)}
          user={userForEmployeeLinks.user}
          userRow={userForEmployeeLinks.userRow}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar usuario</AlertDialogTitle>
            <AlertDialogDescription>
              Estas seguro de que deseas eliminar a <strong>{userToDelete?.name}</strong>? Esta
              accion no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MainSectionSection>
  );
};

export default UsersContainer;
